import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import { AppCheckbox } from "./form/AppCheckbox";

/** Узгоджено зі складом /stocks при порівнянні з `excludedSkuSet`. */
function normSku(s) {
    return String(s || "")
        .trim()
        .normalize("NFKC");
}

function buildVariantRows(allProducts, productId) {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) {
        return [];
    }
    const rows = (allProducts || []).filter((p) => Number(p.product_id) === pid);
    const bySku = new Map();
    for (const row of rows) {
        const sku = String(row.sku || "").trim();
        if (!sku) {
            continue;
        }
        if (bySku.has(sku)) {
            continue;
        }
        const label =
            Array.isArray(row.properties) && row.properties.length
                ? row.properties.map((o) => String(o.value || "").trim()).filter(Boolean).join(" · ")
                : sku;
        bySku.set(sku, {
            sku,
            offerId: row.id,
            label,
        });
    }
    return [...bySku.values()].sort((a, b) => a.label.localeCompare(b.label, "uk"));
}

export default function ProductSewingStatusModal({
    show,
    onHide,
    productId,
    productName,
    allProducts,
    /** Множина SKU у допродажі / виключені з пошиву (SewingExclusion), ключі — `normSku`. */
    excludedSkuSet,
    onApplied,
}) {
    const variants = useMemo(
        () => buildVariantRows(allProducts, productId),
        [allProducts, productId]
    );

    const [checked, setChecked] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    /** Підтягування збереженого стану лише при відкритті або зміні товару в модалці (не під час фонового оновлення списку). */
    const wasModalOpen = useRef(false);
    const lastProductIdInModal = useRef(null);

    useEffect(() => {
        if (!show) {
            wasModalOpen.current = false;
            lastProductIdInModal.current = null;
            return;
        }
        const pidChanged =
            lastProductIdInModal.current !== null &&
            lastProductIdInModal.current !== productId;
        lastProductIdInModal.current = productId;
        const justOpened = !wasModalOpen.current || pidChanged;
        wasModalOpen.current = true;
        if (!justOpened) {
            return;
        }
        const set = excludedSkuSet instanceof Set ? excludedSkuSet : new Set();
        const next = {};
        for (const v of buildVariantRows(allProducts, productId)) {
            next[v.sku] = !set.has(normSku(v.sku));
        }
        setChecked(next);
        setError(null);
    }, [show, productId, allProducts, excludedSkuSet]);

    const tableAllSelected = useMemo(
        () =>
            variants.length > 0 &&
            variants.every((v) => checked[v.sku] === true),
        [variants, checked]
    );
    const tableNoneSelected = useMemo(
        () =>
            variants.length > 0 &&
            variants.every((v) => checked[v.sku] !== true),
        [variants, checked]
    );
    const tableSelectPartial = useMemo(
        () =>
            variants.length > 0 &&
            !tableAllSelected &&
            !tableNoneSelected,
        [variants, tableAllSelected, tableNoneSelected]
    );

    const toggleSku = (sku) => {
        setChecked((prev) => ({ ...prev, [sku]: !prev[sku] }));
    };

    const setAll = (value) => {
        setChecked((prev) => {
            const next = { ...prev };
            for (const k of Object.keys(next)) {
                next[k] = value;
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (tableAllSelected) {
            setAll(false);
        } else {
            setAll(true);
        }
    };

    const postBulkPayload = async (action, skus) => {
        const res = await fetch("/api/sewing-exclusions/bulk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ action, skus }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.error || "Помилка запиту");
        }
        return data;
    };

    /** Галочка = у пошиві (прибрати з SewingExclusion); без галочки = допродаж (додати до SewingExclusion). */
    const applySewingState = async () => {
        if (variants.length === 0) {
            return;
        }
        const toSew = variants.filter((v) => checked[v.sku]).map((v) => v.sku);
        const notSew = variants.filter((v) => !checked[v.sku]).map((v) => v.sku);
        setBusy(true);
        setError(null);
        try {
            if (toSew.length) {
                await postBulkPayload("remove", toSew);
            }
            if (notSew.length) {
                const addData = await postBulkPayload("add", notSew);
                if (Array.isArray(addData.errors) && addData.errors.length > 0) {
                    const msg = addData.errors.map((e) => `${e.sku}: ${e.message}`).join("; ");
                    setError(
                        addData.affected > 0
                            ? `Частково застосовано (додано ${addData.affected}): ${msg}`
                            : `Не застосовано: ${msg}`
                    );
                    if (addData.affected > 0 && typeof onApplied === "function") {
                        await onApplied(addData);
                    }
                    return;
                }
            }
            if (typeof onApplied === "function") {
                await onApplied({});
            }
            onHide();
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            scrollable
            centered
            dialogClassName="product-sew-status-modal__dialog"
        >
            <Modal.Header closeButton>
                <Modal.Title>{productName || `Товар #${productId}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small mb-2">
                    <strong>Галочка</strong> — цей SKU шиємо; <strong>без галочки</strong> — не шиємо (допродаж). На
                    складі біля товару: усі з галочками — <strong>Шиємо</strong>, змішано —{" "}
                    <strong>Частково</strong>, усі без галочок — <strong>Не шиємо</strong>. Натисніть{" "}
                    <strong>Зберегти</strong>, щоб застосувати зміни.
                </p>
                {error && (
                    <div className="alert alert-warning py-2 small mb-2" role="alert">
                        {error}
                    </div>
                )}
                {variants.length === 0 ? (
                    <p className="text-muted mb-0">Немає рядків зі SKU для цього товару.</p>
                ) : (
                    <>
                        <Table
                            bordered
                            size="sm"
                            className="mb-0 align-middle product-sew-status-modal__table"
                        >
                            <colgroup>
                                <col className="product-sew-status-modal__col-check" />
                                <col className="product-sew-status-modal__col-sku" />
                                <col className="product-sew-status-modal__col-variant" />
                            </colgroup>
                            <thead className="table-light">
                                <tr>
                                    <th
                                        scope="col"
                                        className="product-sew-status-modal__th-check align-middle text-center"
                                    >
                                        <div className="small text-secondary fw-semibold mb-1 text-nowrap">
                                            Шиємо
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center flex-nowrap">
                                            <AppCheckbox
                                                id={`product-sew-status-master-${productId}`}
                                                label=""
                                                className="mb-0 flex-shrink-0 product-sew-status-modal__check"
                                                indeterminate={tableSelectPartial}
                                                checked={tableAllSelected}
                                                disabled={variants.length === 0 || busy}
                                                style={{
                                                    cursor:
                                                        variants.length && !busy
                                                            ? "pointer"
                                                            : "not-allowed",
                                                }}
                                                onChange={toggleSelectAll}
                                                title={
                                                    tableSelectPartial
                                                        ? "Обрано частину; клік — обрати всі"
                                                        : tableAllSelected
                                                          ? "Зняти всі"
                                                          : "Обрати всі"
                                                }
                                                aria-label="Увімкнути або вимкнути шиття для всіх SKU"
                                            />
                                        </div>
                                    </th>
                                    <th scope="col" className="product-sew-status-modal__th-sku">
                                        SKU
                                    </th>
                                    <th scope="col" className="product-sew-status-modal__th-variant">
                                        Варіант
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {variants.map((v) => (
                                    <tr key={v.sku}>
                                        <td className="product-sew-status-modal__td-check">
                                            <AppCheckbox
                                                id={`product-sew-cb-${productId}-${v.offerId}`}
                                                checked={Boolean(checked[v.sku])}
                                                onChange={() => toggleSku(v.sku)}
                                                disabled={busy}
                                                className="mb-0 product-sew-status-modal__check"
                                                label={
                                                    <span className="visually-hidden">
                                                        Шиємо: {v.sku}
                                                    </span>
                                                }
                                            />
                                        </td>
                                        <td
                                            className="small text-break product-sew-status-modal__td-sku"
                                            style={{ fontFamily: "ui-monospace, monospace" }}
                                        >
                                            {v.sku}
                                        </td>
                                        <td className="small product-sew-status-modal__td-variant">
                                            {v.label}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="product-sew-status-modal__footer d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center justify-content-md-end border-0 pt-0">
                {busy && (
                    <Spinner
                        animation="border"
                        size="sm"
                        className="align-self-center flex-shrink-0 py-2 py-md-0"
                        role="status"
                    />
                )}
                <Button
                    variant="primary"
                    className="w-100 w-md-auto"
                    disabled={busy || variants.length === 0}
                    onClick={() => void applySewingState()}
                >
                    Зберегти
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
